import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarDays, MapPin, Sparkles, UserPlus } from "lucide-react";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Gala() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("finished");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches?match_type=gala");
        setMatches(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const finished = matches.filter((m) => m.status === "finished");
  const upcoming = matches.filter((m) => m.status !== "finished");
  const list = tab === "finished" ? finished : upcoming;

  return (
    <div className="space-y-8" data-testid="gala-page">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-purple-300 mb-2 flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Événement
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
          Matchs de Gala
        </h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Rencontres exceptionnelles à composition libre — nommez vos deux équipes, invitez des joueurs
          externes et gardez la trace du score final.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <RecordCard label="Programmés" value={matches.length} color="#A855F7" testid="gala-total" />
        <RecordCard label="Terminés" value={finished.length} color="#E5B85C" testid="gala-finished" />
        <RecordCard label="À venir" value={upcoming.length} color="#94A3B8" testid="gala-upcoming" />
      </div>

      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-900 border border-white/10 w-fit">
        {[
          { key: "finished", label: `Terminés (${finished.length})` },
          { key: "upcoming", label: `À venir (${upcoming.length})` },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`gala-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide ${
              tab === t.key
                ? "bg-purple-500 text-white"
                : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : list.length === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
          {tab === "finished"
            ? "Aucun match de gala terminé pour le moment"
            : "Aucun match de gala à venir. Créez-en un depuis la page Matchs."}
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((m) => (
            <GalaCard key={m.id} m={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function GalaCard({ m }) {
  const isFinished = m.status === "finished";
  const a = m.score_team_a ?? 0;
  const b = m.score_team_b ?? 0;
  const guestCount = Array.isArray(m.guests) ? m.guests.length : 0;
  return (
    <div
      data-testid={`gala-match-${m.id}`}
      className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden"
    >
      <div className="p-5 flex items-start gap-4 flex-wrap justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/40">
              Gala
            </span>
            {guestCount > 0 && (
              <span className="text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-white/10 inline-flex items-center gap-1">
                <UserPlus className="w-3 h-3" /> {guestCount} invité{guestCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <Link
            to={`/matches/${m.id}`}
            className="font-display font-black text-xl sm:text-2xl uppercase tracking-tight hover:text-purple-300"
          >
            {m.title ? `${m.title} · ` : ""}
            {m.team_a_name || "Équipe A"} vs {m.team_b_name || "Équipe B"}
          </Link>
          <div className="flex flex-wrap gap-3 text-xs text-slate-400 font-mono-stat mt-2">
            <span className="flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> {formatDate(m.match_date)}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {m.location}
            </span>
          </div>
        </div>
        {isFinished ? (
          <div
            data-testid={`gala-score-${m.id}`}
            className="font-mono-stat font-black text-2xl sm:text-3xl flex items-center gap-3 whitespace-nowrap"
          >
            <span className="text-purple-300">{m.team_a_name} {a}</span>
            <span className="text-slate-600">—</span>
            <span className="text-fuchsia-300">{b} {m.team_b_name}</span>
          </div>
        ) : (
          <Link
            to={`/matches/${m.id}`}
            className="text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white"
          >
            Ouvrir
          </Link>
        )}
      </div>
    </div>
  );
}

function RecordCard({ label, value, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-4 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: color }} />
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="font-display font-black text-3xl mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
